'use client';
import dynamic from 'next/dynamic';
import DefaultBreadcrumb from '@/components/common/breadcrumbs';
import TestimonialVideo from '@/components/home/testimonial-video';
import TestimonialCaption from '@/components/home/testimonial-caption';
import React, { useEffect, useState } from 'react';
import { getRequest } from '@/api/helpers';
import { routes } from '@/api/routes';
import NoRecord from '@/components/common/no-record';
import { isEmpty } from '@/utils/common-helpers';
import MetaTags from '@/components/meta-tag/meta-tag';
import 'react-loading-skeleton/dist/skeleton.css';
import BorderEdge from '@/components/common/border-edge';

const CenterHeroSection = dynamic(() => import('@/components/center-hero'));
const ContactInfo = dynamic(() => import('@/components/common/contact-info'));
const ContactForm = dynamic(() => import('@/components/common/contact-form'));
const Skeleton = dynamic(() => import('react-loading-skeleton'));

const Page = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [noRecords, setNoRecords] = useState(false);

    useEffect(() => {
        fetchTestimonials().then();
    }, []);

    const fetchTestimonials = async () => {
        setFetching(true);
        const response = await getRequest(routes.TESTIMONIALS);

        if (response && response.data && response.data.length > 0) {
            setTestimonials(response.data);
        } else {
            setNoRecords(false);
        }
        setFetching(false);
    };

    const TestimonialSkeletons = () => {
        return (
            <>
                <Skeleton height="350px" />
                <Skeleton height="350px" />
                <Skeleton height="350px" />
                <Skeleton height="350px" />
                <Skeleton height="350px" />
                <Skeleton height="350px" />
            </>
        );
    };

    const playPauseVideo = (playOneVideo?: boolean, id?: string) => {
        let videos = document.querySelectorAll('video');

        if (playOneVideo) {
            videos.forEach((video) => {
                if (video.id !== id) {
                    video.pause();
                }
            });
        } else {
            videos.forEach((video) => {
                let observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.intersectionRatio !== 1 && !video.paused) {
                                video.pause();
                            }
                        });
                    },
                    { threshold: 0.2 },
                );
                observer.observe(video);
            });
        }
    };

    return (
        <>
            <MetaTags />
            <CenterHeroSection
                className=""
                pageTitle="What Our Happy Clients Say"
                desc="Read client reviews about our services & their experience throughout the project. These customer reviews will definitely help you to make your decision smartly while choosing us over other software development companies."
            />
            <DefaultBreadcrumb currentPage="Client Testimonials" />
            <section className="pt-100 pb-14 client-testimonial bg-grey mb-0.5">
                {noRecords ? (
                    <NoRecord />
                ) : (
                    <div className="container lg:container md:mx-auto">
                        <div onWheel={() => playPauseVideo()} className="md:columns-2 lg:columns-3 gap-10">
                            {fetching ? (
                                <TestimonialSkeletons />
                            ) : (
                                testimonials.map((testimonial: any, index: number) => {
                                    const name = testimonial.client_name;
                                    const post = testimonial.project_name;
                                    const designation = testimonial.client_designation;

                                    if (isEmpty(testimonial.video_url)) {
                                        return (
                                            <TestimonialCaption
                                                key={index}
                                                name={name}
                                                post={post}
                                                para={testimonial.description}
                                            />
                                        );
                                    } else {
                                        return (
                                            <TestimonialVideo
                                                key={index}
                                                name={name}
                                                post={designation}
                                                videoUrl={testimonial.video_url}
                                                id={`testimonial_video_${index}`}
                                                onPlay={() => playPauseVideo(true, `testimonial_video_${index}`)}
                                            />
                                        );
                                    }
                                })
                            )}
                        </div>
                    </div>
                )}
            </section>
            <BorderEdge color="text-white" bg="bg-blue" />
            <div className="section bg-primary py-100 dark:text-light dark:bg-black">
                <div className="container lg:container md:mx-auto">
                    <div className="row grid md:grid-cols-12 lg:grid-cols-12">
                        <ContactForm />
                        <ContactInfo />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Page;
